import { Autocomplete, Checkbox, Chip, TextField } from "@mui/material";
import { Check } from "lucide-react";
import { useFormContext } from "react-hook-form";

type Option = {
    value: string | number;
    label: string;
};

type Props = {
    name: string;
    label: string;
    options: Option[];
};

const SELECT_ALL_VALUE = "__SELECT_ALL__";

const MultiSelect = ({ name, label, options }: Props) => {
    const form = useFormContext();

    const fieldValue = (form.watch(name) ?? []) as (string | number)[];

    const handleSelection = (values: (string | number)[]) => {
        form.setValue(name, values);
    };

    const getSelectedOptions = () => {
        return options.filter((option) => fieldValue.includes(option.value));
    };

    const allSelected = fieldValue.length > 0 && fieldValue.length === options.length;

    const selectAllOption: Option = {
        value: SELECT_ALL_VALUE,
        label: allSelected ? "✓ Deselect All" : "Select All",
    };

    const optionsWithSelectAll = [selectAllOption, ...options];

    return (
        <Autocomplete
            multiple
            options={optionsWithSelectAll}
            value={getSelectedOptions()}
            onChange={(_event, newValue) => {
                // Check if Select All was clicked
                const hasSelectAll = newValue.some((item) => item.value === SELECT_ALL_VALUE);

                if (hasSelectAll) {
                    // Toggle: if all selected, deselect all; otherwise select all
                    if (allSelected) {
                        handleSelection([]);
                    } else {
                        handleSelection(options.map((opt) => opt.value));
                    }
                } else {
                    // Normal selection - filter out the Select All option from values
                    const normalValues = newValue
                        .filter((item) => item.value !== SELECT_ALL_VALUE)
                        .map((item) => item.value);
                    handleSelection(normalValues);
                }
            }}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            renderOption={(props, option, { selected }) => {
                const isSelectAll = option.value === SELECT_ALL_VALUE;
                const { key, ...otherProps } = props;

                return (
                    <li {...otherProps} key={option.value}>
                        <Checkbox
                            icon={
                                <span
                                    style={{
                                        width: 18,
                                        height: 18,
                                        border: "2px solid #ccc",
                                        borderRadius: 3,
                                    }}
                                />
                            }
                            checkedIcon={<Check size={18} />}
                            style={{ marginRight: 8 }}
                            checked={isSelectAll ? allSelected : selected}
                        />
                        <span style={{ fontWeight: isSelectAll ? 600 : 400 }}>{option.label}</span>
                    </li>
                );
            }}
            renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                    <Chip
                        variant="outlined"
                        label={option.label}
                        {...getTagProps({ index })}
                        key={option.value}
                    />
                ))
            }
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    placeholder={label}
                    error={!!form.formState.errors[name]}
                    helperText={form.formState.errors[name]?.message as string}
                />
            )}
            sx={{
                width: "100%",
                backgroundColor: "#fff",
                "& .MuiInputBase-root": {
                    minHeight: 56,
                },
            }}
        />
    );
};

export default MultiSelect;
